/**
 * The Code tab's History (AIOE 0.65.0): a repository's commit graph with its branches, read through the project's
 * GitHub connection (routes/projectGit.ts). Each commit says who its work came from, an agent or a person (the
 * workbench's commitAttribution, worked out by the platform with the project's teams); a commit opens with its diff
 * (ReviewPatch); a branch or a tag narrows the graph; two refs compare. Lanes come from gitGraphLayout.ts, copied
 * from the workbench as it is.
 */

import { matchesAttribution, type AttributionFilter, type CommitAttribution, type GitFileChange, type GraphCommitPayload, type ProjectGitBranch, type ProjectGitCommits, type ProjectRoot, type ProjectSource, type WorkStage, repositoryForRemote } from '@aioe/shared';
import { time } from '@aioe/shared/wording/dateTime';
import { ArrowUpRightFromSquare, BranchesRight, ClockArrowRotateLeft, CodeCompare, Cpu, Gear, Sparkles, Tag } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { Sheet, Tooltip } from '@heroui/react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, routes } from '../../api/client.ts';
import { dateTimeWords, timeAgo, useLoad } from '../../hooks.ts';
import { FileDiffs, LineCounts } from '../review/FileDiffs.tsx';
import { Button, ChoiceSelect, ErrorNote, ListBlock, ListGroup, SectionHeading } from '../ui.tsx';
import { ArtEmpty, VIEW_TONES } from '../ui/Art.tsx';
import { BranchMap, hasBranchMap } from './BranchMap.tsx';
import { type GraphRow, laneColour, layoutCommitGraph } from './gitGraphLayout.ts';
import './history.css';

/** The project page's own tone, as every part of it uses. */
const TONE = VIEW_TONES.projects!;
const ALL = '__every-branch';
const PAGE = 50;
const ROW_H = 46;
const LANE_W = 14;

// ── Words ────────────────────────────────────────────────────────────────

/** Who did it, in a few words: "Mira", "Claude", "Jean-Pierre Simonis". */
const whoShort = (a: CommitAttribution | undefined, author: string) => (a?.kind === 'workbench' ? 'AI Workbench' : (a?.name ?? author));

/** Who did it, as a sentence for a hover and the commit's sheet. */
export function attributionWords(a: CommitAttribution | undefined, author: string): string {
  switch (a?.kind) {
    case 'member':
      return `${a.name}, an agent${a.teamName ? ` of ${a.teamName}` : a.teamId ? ` of ${a.teamId}` : ''}${a.plan ? `, working on ${a.plan}` : ''}, for ${author}`;
    case 'specialist':
      return `${a.name}, a specialist agent, for ${author}`;
    case 'external-ai':
      return `${a.tool ?? a.name}, an AI the workbench did not run, with ${author}`;
    case 'workbench':
      return `AI Workbench's own housekeeping, for ${author}`;
    default:
      return `${author}, a person`;
  }
}

const STAGE_WORDS: Record<WorkStage, string> = { working: 'Being worked on', landing: 'Approved — going in', plan: "In the plan's branch", main: 'In the default branch' };

const initials = (name: string) => {
  const w = name.trim().split(/[\s._@-]+/).filter(Boolean);
  return !w.length ? '?' : w.length === 1 ? w[0]!.slice(0, 2).toUpperCase() : (w[0]![0]! + w[w.length - 1]![0]!).toUpperCase();
};

/** The shape that says who: a rounded square with a chip for an agent, dashed with sparkles for an outside AI, initials in a circle for a person. */
export function AttributionMark({ a, author }: { a: CommitAttribution | undefined; author: string }) {
  const kind = a?.kind ?? 'person';
  return (
    <Tooltip delay={300}>
      <Tooltip.Trigger className="history-mark" data-kind={kind} aria-label={attributionWords(a, author)}>
        {kind === 'member' || kind === 'specialist' ? <Cpu aria-hidden /> : kind === 'external-ai' ? <Sparkles aria-hidden /> : kind === 'workbench' ? <Gear aria-hidden /> : initials(a?.name ?? author)}
      </Tooltip.Trigger>
      <Tooltip.Content>{attributionWords(a, author)}</Tooltip.Content>
    </Tooltip>
  );
}

// ── The graph ────────────────────────────────────────────────────────────

const laneX = (lane: number) => 7 + lane * LANE_W;

function GraphSlice({ row, width, head }: { row: GraphRow; width: number; head: boolean }) {
  const mid = ROW_H / 2;
  return (
    <svg className="history-row__graph" width={width} height={ROW_H} aria-hidden="true">
      {row.lines.map((l, i) => (
        <line key={`l${i}`} x1={laneX(l.lane)} x2={laneX(l.lane)} y1={l.part === 'bottom' ? mid : 0} y2={l.part === 'top' ? mid : ROW_H} stroke={laneColour(l.colour)} strokeWidth={1.5} />
      ))}
      {row.elbows.map((e, i) => {
        const a = laneX(e.from);
        const b = laneX(e.to);
        const d = e.kind === 'in' ? `M ${a} 0 Q ${a} ${mid} ${b} ${mid}` : `M ${a} ${mid} Q ${b} ${mid} ${b} ${ROW_H}`;
        return <path key={`e${i}`} d={d} fill="none" stroke={laneColour(e.colour)} strokeWidth={1.5} />;
      })}
      {head ? <circle cx={laneX(row.lane)} cy={mid} r={7} fill="none" stroke={laneColour(row.colour)} strokeWidth={1.5} /> : null}
      <circle cx={laneX(row.lane)} cy={mid} r={4.5} fill={laneColour(row.colour)} stroke="var(--background)" strokeWidth={1.5} />
    </svg>
  );
}

function RefPills({ commit, defaultBranch }: { commit: GraphCommitPayload; defaultBranch: string }) {
  if (!commit.refs.length) return null;
  const shown = commit.refs.slice(0, 2);
  return (
    <>
      {shown.map((r) => (
        <span key={`${r.kind}:${r.name}`} className="history-ref" data-default={(r.kind === 'branch' && r.name === defaultBranch) || undefined} title={r.kind === 'tag' ? `Tag ${r.name}` : `Branch ${r.name}`}>
          {r.kind === 'tag' ? <Tag aria-hidden /> : <BranchesRight aria-hidden />}
          {r.name}
        </span>
      ))}
      {commit.refs.length > shown.length ? <span className="text-2xs text-muted">+{commit.refs.length - shown.length}</span> : null}
    </>
  );
}

function CommitRow({ commit, row, width, head, dim, selected, defaultBranch, onOpen }: { commit: GraphCommitPayload; row: GraphRow; width: number; head: boolean; dim: boolean; selected: boolean; defaultBranch: string; onOpen: () => void }) {
  const who = whoShort(commit.attribution, commit.author);
  const forWhom = commit.attribution && commit.attribution.kind !== 'person' && commit.author !== who ? ` for ${commit.author}` : '';
  return (
    <li>
      <button type="button" className="history-row" style={{ height: ROW_H }} data-selected={selected || undefined} data-dim={dim || undefined} onClick={onOpen}>
        <GraphSlice row={row} width={width} head={head} />
        <AttributionMark a={commit.attribution} author={commit.author} />
        <span className="history-row__words">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate text-sm font-medium">{commit.subject || '(no message)'}</span>
            <RefPills commit={commit} defaultBranch={defaultBranch} />
          </span>
          <span className="truncate text-xs text-muted">
            {who}
            {forWhom}
            {commit.task ? ` · ${commit.task.taskId}` : ''} · <span className="tabular-nums" title={dateTimeWords(commit.at)}>{timeAgo(new Date(commit.at).toISOString())}</span> · <span className="font-mono">{commit.short}</span>
          </span>
        </span>
      </button>
    </li>
  );
}

// ── A commit, and two refs compared ─────────────────────────────────────

function CommitSheet({ projectId, rootId, commit, defaultBranch, onClose }: { projectId: string; rootId: string; commit: GraphCommitPayload; defaultBranch: string; onClose: () => void }) {
  const detail = useLoad(() => routes.gitCommit(projectId, rootId, commit.hash), [projectId, rootId, commit.hash]);
  const d = detail.data;
  return (
    <Sheet isOpen onOpenChange={(o) => !o && onClose()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="w-full max-w-3xl">
          <Sheet.Dialog aria-label={`Commit ${commit.short}`}>
            <Sheet.Header>
              <Sheet.Heading className="pe-8">{commit.subject || '(no message)'}</Sheet.Heading>
            </Sheet.Header>
            <Sheet.Body className="flex flex-col gap-4 overflow-y-auto">
              <div className="flex items-start gap-3">
                <AttributionMark a={commit.attribution} author={commit.author} />
                <div className="grid min-w-0 gap-0.5 text-sm">
                  <span>{attributionWords(commit.attribution, commit.author)}</span>
                  <span className="text-xs text-muted">
                    {dateTimeWords(commit.at)} · <span className="font-mono">{commit.short}</span>
                    {commit.stage ? ` · ${commit.stage === 'main' ? `In ${defaultBranch}` : STAGE_WORDS[commit.stage]}` : ''}
                    {commit.task ? ` · ${commit.task.taskId} ${commit.task.title}` : ''}
                  </span>
                </div>
              </div>
              {d?.body ? <p className="whitespace-pre-wrap text-sm">{d.body}</p> : null}
              {commit.url ? (
                <a href={commit.url} target="_blank" rel="noreferrer" className="inline-flex w-fit items-center gap-1 text-sm text-accent hover:underline">
                  Open on GitHub <ArrowUpRightFromSquare className="size-3.5" aria-hidden />
                </a>
              ) : null}
              <ErrorNote error={detail.error} />
              {detail.loading && !d ? <p className="text-sm text-muted">Reading the commit from GitHub…</p> : null}
              {d ? (
                <div className="grid gap-2">
                  <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
                    <span className="font-medium">
                      {d.files.length} {d.files.length === 1 ? 'file' : 'files'}
                      {d.merge ? ' brought in by this merge' : ''}
                    </span>
                    <LineCounts additions={d.insertions} deletions={d.deletions} />
                  </div>
                  {d.filesTruncated ? <p className="text-xs text-muted">GitHub lists only the first {d.files.length} files of a commit this large.</p> : null}
                  {d.patchTruncated ? <p className="text-xs text-muted">Some files are shown without their lines, so the page stays quick to load.</p> : null}
                  <FileDiffs key={d.hash} files={d.files} webUrl={commit.url} />
                </div>
              ) : null}
            </Sheet.Body>
            <Sheet.CloseTrigger />
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}

function compareSentence(c: { status: string; aheadBy: number; behindBy: number; base: string; head: string }): string {
  const n = (k: number) => `${k} ${k === 1 ? 'commit' : 'commits'}`;
  if (c.status === 'identical') return `${c.head} and ${c.base} are the same.`;
  if (c.status === 'ahead') return `${c.head} is ${n(c.aheadBy)} ahead of ${c.base}.`;
  if (c.status === 'behind') return `${c.head} is ${n(c.behindBy)} behind ${c.base}, with nothing of its own.`;
  return `${c.head} has ${n(c.aheadBy)} that ${c.base} does not, and ${c.base} has ${n(c.behindBy)} that it does not.`;
}

function CompareSheet({ projectId, rootId, refs, initial, onClose }: { projectId: string; rootId: string; refs: Record<string, string>; initial: { base: string; head: string }; onClose: () => void }) {
  const [base, setBase] = useState(initial.base);
  const [head, setHead] = useState(initial.head);
  const ready = Boolean(base && head && base !== head);
  const result = useLoad(() => (ready ? routes.gitCompare(projectId, rootId, base, head) : Promise.resolve(undefined)), [projectId, rootId, base, head]);
  const c = result.data;
  return (
    <Sheet isOpen onOpenChange={(o) => !o && onClose()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="w-full max-w-3xl">
          <Sheet.Dialog aria-label="Compare two branches">
            <Sheet.Header>
              <Sheet.Heading>Compare</Sheet.Heading>
            </Sheet.Header>
            <Sheet.Body className="flex flex-col gap-4 overflow-y-auto">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <ChoiceSelect label="Compare from" className="w-fit min-w-40 max-w-full" value={base} options={refs} onChange={setBase} />
                <span className="text-muted">to</span>
                <ChoiceSelect label="Compare to" className="w-fit min-w-40 max-w-full" value={head} options={refs} onChange={setHead} />
              </div>
              {!ready ? <p className="text-sm text-muted">Choose two different branches or tags.</p> : null}
              <ErrorNote error={result.error} />
              {ready && result.loading && !c ? <p className="text-sm text-muted">Comparing on GitHub…</p> : null}
              {ready && c && c.base === base && c.head === head ? (
                <>
                  <p className="text-sm">{compareSentence(c)}</p>
                  {c.commits.length ? (
                    <ul className="grid gap-1.5">
                      {c.commits.map((x) => (
                        <li key={x.hash} className="flex min-w-0 items-center gap-2 text-sm">
                          <AttributionMark a={x.attribution} author={x.author} />
                          <span className="min-w-0 flex-1 truncate">{x.subject}</span>
                          <span className="shrink-0 font-mono text-xs text-muted">{x.short}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {c.commitsTruncated ? <p className="text-xs text-muted">The newest {c.commits.length} of {c.totalCommits} commits.</p> : null}
                  <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
                    <span className="font-medium">
                      {c.files.length} {c.files.length === 1 ? 'file' : 'files'} changed
                    </span>
                    <LineCounts additions={sum(c.files, 'additions')} deletions={sum(c.files, 'deletions')} />
                    <a href={c.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                      Open on GitHub
                    </a>
                  </div>
                  {c.filesTruncated ? <p className="text-xs text-muted">GitHub lists only the first {c.files.length} files of a comparison this large.</p> : null}
                  {c.patchTruncated ? <p className="text-xs text-muted">Some files are shown without their lines, so the page stays quick to load.</p> : null}
                  <FileDiffs key={`${base}...${head}`} files={c.files} webUrl={c.url} />
                </>
              ) : null}
            </Sheet.Body>
            <Sheet.CloseTrigger />
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}

const sum = (files: GitFileChange[], k: 'additions' | 'deletions') => files.reduce((n, f) => n + f[k], 0);

// ── The part ─────────────────────────────────────────────────────────────

type Problem = { code: string; message: string };
const problemOf = (e: unknown): Problem => (e instanceof ApiError ? { code: e.code, message: e.message } : { code: 'error', message: (e as Error).message });

const WHO: Array<{ id: AttributionFilter; label: string }> = [
  { id: 'everyone', label: 'Everyone' },
  { id: 'team', label: 'Agents' },
  { id: 'people', label: 'People' },
  { id: 'outside', label: 'Other AI' },
];

/** The project's repositories that are on its GitHub connection, one entry per root. */
function githubRoots(roots: ProjectRoot[], source: ProjectSource | null): Array<{ rootId: string; name: string; primary: boolean }> {
  if (source?.provider !== 'github') return [];
  const out = new Map<string, { rootId: string; name: string; primary: boolean }>();
  for (const r of roots) {
    const repo = repositoryForRemote(source, r.remote);
    if (repo && !out.has(r.rootId)) out.set(r.rootId, { rootId: r.rootId, name: r.name?.trim() || repo.name, primary: repo.primary });
  }
  return [...out.values()].sort((a, b) => Number(b.primary) - Number(a.primary) || a.name.localeCompare(b.name));
}

export function CodeHistory({ projectId, roots, source, owner, onConnect }: { projectId: string; roots: ProjectRoot[]; source: ProjectSource | null; owner: boolean; onConnect: () => void }) {
  const repos = githubRoots(roots, source);
  const [picked, setPicked] = useState<string>();
  const rootId = repos.find((r) => r.rootId === picked)?.rootId ?? repos[0]?.rootId;
  if (source?.provider !== 'github')
    return (
      <ArtEmpty
        tone={TONE}
        icon={<ClockArrowRotateLeft />}
        title="Connect GitHub to see the history"
        action={
          owner ? (
            <Button size="sm" variant="secondary" onPress={onConnect}>
              Open Repositories
            </Button>
          ) : undefined
        }
      >
        {owner
          ? "The history comes from GitHub. Choose the project's GitHub connection under Repositories and pick its repositories; an administrator adds GitHub under Settings, Integrations first."
          : 'The history comes from GitHub. An owner of the project connects it under Code, Repositories.'}
      </ArtEmpty>
    );
  if (!rootId)
    return (
      <ArtEmpty tone={TONE} icon={<ClockArrowRotateLeft />} title="No repository to show yet">
        {source.repositories.length
          ? "None of the project's GitHub repositories is open on a workbench or a node yet. Once a member opens one, its history shows here."
          : 'The project names no GitHub repositories yet. An owner picks them under Code, Repositories.'}
      </ArtEmpty>
    );
  return (
    <>
      {repos.length > 1 ? <SectionHeading title="Repository" description="Each of the project's GitHub repositories has its own history." actions={<ChoiceSelect label="Repository" value={rootId} options={Object.fromEntries(repos.map((r) => [r.rootId, r.name]))} onChange={setPicked} />} /> : null}
      <RepositoryHistory key={rootId} projectId={projectId} rootId={rootId} />
    </>
  );
}

function RepositoryHistory({ projectId, rootId }: { projectId: string; rootId: string }) {
  const [ref, setRef] = useState(ALL);
  const [who, setWho] = useState<AttributionFilter>('everyone');
  const [open, setOpen] = useState<GraphCommitPayload>();
  const [comparing, setComparing] = useState(false);
  const branches = useLoad(() => routes.gitBranches(projectId, rootId).catch((e: unknown) => ({ problem: problemOf(e) })), [projectId, rootId]);
  const tags = useLoad(() => routes.gitTags(projectId, rootId).catch(() => undefined), [projectId, rootId]);
  const [pages, setPages] = useState<{ key: string; answers: ProjectGitCommits[] }>({ key: '', answers: [] });
  const [problem, setProblem] = useState<Problem>();
  const [loadingMore, setLoadingMore] = useState(false);
  const key = `${rootId}|${ref}`;

  useEffect(() => {
    let live = true;
    routes
      .gitCommits(projectId, rootId, { ...(ref !== ALL ? { branch: ref } : {}), limit: PAGE })
      .then((a) => live && (setPages({ key, answers: [a] }), setProblem(undefined)))
      .catch((e: unknown) => live && setProblem(problemOf(e)));
    return () => {
      live = false;
    };
  }, [projectId, rootId, ref, key]);

  const answers = useMemo(() => (pages.key === key ? pages.answers : []), [pages, key]);
  const last = answers.at(-1);
  const commits = useMemo(() => {
    const seen = new Set<string>();
    return answers.flatMap((a) => a.commits).filter((c) => (seen.has(c.hash) ? false : (seen.add(c.hash), true)));
  }, [answers]);
  const layout = useMemo(() => layoutCommitGraph(commits), [commits]);
  const width = 14 + layout.laneCount * LANE_W - 6;

  const more = async () => {
    if (!last) return;
    setLoadingMore(true);
    try {
      const next = await routes.gitCommits(projectId, rootId, { ...(ref !== ALL ? { branch: ref } : {}), limit: PAGE, page: answers.length + 1 });
      setPages((p) => (p.key === key ? { key, answers: [...p.answers, next] } : p));
    } catch (e) {
      setProblem(problemOf(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const b = branches.data && 'branches' in branches.data ? branches.data : undefined;
  const branchProblem = branches.data && 'problem' in branches.data ? branches.data.problem : undefined;
  const failure = problem ?? branchProblem;
  if (failure && (failure.code === 'no_github' || failure.code === 'not_linked'))
    return (
      <ArtEmpty
        tone={TONE}
        icon={<ClockArrowRotateLeft />}
        title={failure.code === 'not_linked' ? 'Link your GitHub account' : 'Not on GitHub here'}
        action={
          failure.code === 'not_linked' ? (
            <Link to="/settings/accounts" className="text-accent hover:underline">
              Open Settings
            </Link>
          ) : undefined
        }
      >
        {failure.message}
      </ArtEmpty>
    );

  const defaultBranch = last?.repository.defaultBranch ?? b?.repository.defaultBranch ?? 'main';
  const refOptions: Record<string, string> = { [ALL]: 'Every branch', ...Object.fromEntries((b?.branches ?? []).map((x) => [x.name, x.name])), ...Object.fromEntries((tags.data?.tags ?? []).map((t) => [t.name, `${t.name} (tag)`])) };
  const compareRefs = Object.fromEntries(Object.entries(refOptions).filter(([k]) => k !== ALL));
  const compareStart = { base: defaultBranch, head: ref !== ALL && ref !== defaultBranch ? ref : ((b?.branches ?? []).find((x: ProjectGitBranch) => !x.default)?.name ?? '') };
  const shown = commits.filter((c) => matchesAttribution(c.attribution, who)).length;
  const repo = last?.repository ?? b?.repository;
  const rate = last?.rateLimit;
  const hint = repo ? `${repo.repository} on GitHub, read with ${repo.readWith === 'you' ? 'your own account' : "the organisation's connection"}` : 'Read from GitHub';

  return (
    <>
      {b && hasBranchMap(b.branches) ? (
        <ListGroup title="Branches" count={b.branches.length} description="Where the work is on its way in; pick one to see its history.">
          <ListBlock>
            <BranchMap branches={b.branches} selected={ref === ALL ? undefined : ref} onPick={(name) => setRef(name)} />
          </ListBlock>
        </ListGroup>
      ) : null}
      <ListGroup
        title="History"
        count={commits.length || undefined}
        description={`${hint}.`}
        actions={
          <Button size="sm" variant="secondary" isDisabled={Object.keys(compareRefs).length < 2} onPress={() => setComparing(true)}>
            <CodeCompare aria-hidden /> Compare
          </Button>
        }
      >
        <ListBlock className="flex flex-wrap items-center gap-2">
          <ChoiceSelect label="Branch or tag" className="w-fit min-w-44 max-w-full" value={ref in refOptions ? ref : ALL} options={refOptions} onChange={setRef} />
          <Segment aria-label="Whose commits" size="sm" selectedKey={who} onSelectionChange={(k) => setWho(String(k) as AttributionFilter)} className="w-fit">
            {WHO.map((w) => (
              <Segment.Item key={w.id} id={w.id}>
                {w.label}
              </Segment.Item>
            ))}
          </Segment>
          {who !== 'everyone' && commits.length ? (
            <span className="text-xs text-muted tabular-nums">
              {shown} of {commits.length}
            </span>
          ) : null}
        </ListBlock>
        {failure ? (
          <ListBlock>
            <ErrorNote error={failure.message} />
          </ListBlock>
        ) : !answers.length ? (
          <ListBlock className="text-sm text-muted">Reading the history from GitHub…</ListBlock>
        ) : !commits.length ? (
          <ListBlock className="text-sm text-muted">No commits on this branch.</ListBlock>
        ) : null}
        {!failure && commits.length ? (
          <ListBlock flush>
            <ul className="m-0 list-none p-0">
              {commits.map((c, i) => (
                <CommitRow key={c.hash} commit={c} row={layout.rows[i]!} width={width} head={c.hash === last?.head} dim={!matchesAttribution(c.attribution, who)} selected={open?.hash === c.hash} defaultBranch={defaultBranch} onOpen={() => setOpen(c)} />
              ))}
            </ul>
          </ListBlock>
        ) : null}
        {!failure && commits.length ? (
            <ListBlock className="flex flex-wrap items-center gap-3 text-xs text-muted">
              {last?.more ? (
                <Button size="sm" variant="ghost" isPending={loadingMore} onPress={() => void more()}>
                  Show older commits
                </Button>
              ) : (
                <span>The beginning of {ref === ALL ? 'the history read' : ref}.</span>
              )}
              {last?.branchesLeftOut ? <span>{last.branchesLeftOut} more branches are not drawn; pick one above to see it.</span> : null}
              {rate && rate.remaining < 200 ? (
                <span className="tabular-nums">
                  GitHub allows {rate.remaining.toLocaleString('en-AU')} more requests on this connection until {time(rate.resetAt)}.
                </span>
              ) : null}
            </ListBlock>
        ) : null}
      </ListGroup>
      {open ? <CommitSheet projectId={projectId} rootId={rootId} commit={open} defaultBranch={defaultBranch} onClose={() => setOpen(undefined)} /> : null}
      {comparing ? <CompareSheet projectId={projectId} rootId={rootId} refs={compareRefs} initial={compareStart} onClose={() => setComparing(false)} /> : null}
    </>
  );
}
